import BN from "bn.js";
import Decimal from "decimal.js";
import { TickMath, ClmmPoolUtil } from "@cetusprotocol/cetus-sui-clmm-sdk";

type BasicInfo = {
  coinType: string;
  decimals: number;
};

const D = (x: string | number) => new Decimal(x);

function normalizeSuiType(t: string) {
  const s = (t || "").trim();
  if (s === "0x2::sui::SUI") {
    return "0x0000000000000000000000000000000000000000000000000000000000000002::sui::SUI";
  }
  return s;
}

// Cetus 排序：逐字符 ASCII，较大者为 coin_a
function compareAscii(a: string, b: string) {
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i++) {
    const ca = a.charCodeAt(i);
    const cb = b.charCodeAt(i);
    if (ca !== cb) return ca - cb;
  }
  return a.length - b.length;
}

function toBaseUnits(amountHuman: string, decimals: number): BN {
  const v = D(amountHuman).mul(D(10).pow(decimals)).toFixed(0, Decimal.ROUND_DOWN);
  return new BN(v);
}

function fromBaseUnits(amount: BN, decimals: number): string {
  return D(amount.toString()).div(D(10).pow(decimals)).toString();
}

/**
 * 你的语义：initPrice = token1Amount / token2Amount （token1 per token2）
 * 默认区间：±10%（也按 token1 per token2 的方向浮动）
 */
export function quoteToken2FromToken1_WithSort(params: {
  basicInfo1: BasicInfo;                // token1
  basicInfo2: BasicInfo;                // token2
  token1Amount: string;                 // 人类单位，属于 token1
  initPriceToken1PerToken2: string;     // ✅ token1/token2
  tickSpacing: number;
  rangePct?: number;                    // 默认 0.10
  slippage?: number;                    // 默认 0.005
}) {
  const {
    basicInfo1,
    basicInfo2,
    token1Amount,
    initPriceToken1PerToken2,
    tickSpacing,
    rangePct = 0.10,
    slippage = 0.005,
    // slippage = 0,
  } = params;

  const token1 = normalizeSuiType(basicInfo1.coinType);
  const token2 = normalizeSuiType(basicInfo2.coinType);

  const P12 = D(initPriceToken1PerToken2); // token1 per token2
  if (P12.lte(0)) throw new Error("initPrice must be > 0");
  if (rangePct < 0 || rangePct >= 1) throw new Error("rangePct must be in (0,1)");

  // 你的区间：按 token1/token2 ±10%
  const P12_min = P12.mul(D(1).minus(rangePct));
  const P12_max = P12.mul(D(1).plus(rangePct));

  // 1) Cetus 排序 coin_a/coin_b
  const coin_a = compareAscii(token1, token2) > 0 ? token1 : token2;
  const coin_b = coin_a === token1 ? token2 : token1;

  // 2) 对应 decimalsA/decimalsB
  const decimalsA = coin_a === token1 ? basicInfo1.decimals : basicInfo2.decimals;
  const decimalsB = coin_b === token1 ? basicInfo1.decimals : basicInfo2.decimals;

  // 3) 转成 SDK 需要的价格方向：coin_b per coin_a
  // 情况A：coin_a = token1, coin_b = token2
  //   coin_b/coin_a = token2/token1 = 1/(token1/token2) = 1/P12
  // 情况B：coin_a = token2, coin_b = token1
  //   coin_b/coin_a = token1/token2 = P12
  const coinAIsToken1 = coin_a === token1;

  const priceBA = coinAIsToken1 ? D(1).div(P12) : P12;

  // min/max 也要跟着方向转换；注意倒数会交换大小
  const priceBA_min = coinAIsToken1 ? D(1).div(P12_max) : P12_min;
  const priceBA_max = coinAIsToken1 ? D(1).div(P12_min) : P12_max;

  // 4) price -> sqrtPrice -> tick，并按 tickSpacing 对齐
  const curSqrt = new BN(TickMath.priceToSqrtPriceX64(priceBA, decimalsA, decimalsB).toString());
  const sqrtMin = new BN(TickMath.priceToSqrtPriceX64(priceBA_min, decimalsA, decimalsB).toString());
  const sqrtMax = new BN(TickMath.priceToSqrtPriceX64(priceBA_max, decimalsA, decimalsB).toString());

  const tickMin = TickMath.sqrtPriceX64ToTickIndex(sqrtMin);
  const tickMax = TickMath.sqrtPriceX64ToTickIndex(sqrtMax);

  const tickLower = TickMath.getPrevInitializableTickIndex(tickMin, tickSpacing);
  const tickUpper = TickMath.getNextInitializableTickIndex(tickMax, tickSpacing);
  if (tickLower >= tickUpper) throw new Error("Invalid tick range after alignment");

  // 5) 固定 token1 金额
  const token1Base = toBaseUnits(token1Amount, basicInfo1.decimals);

  // fix_amount_a：固定侧是否为 coin_a
  const fix_amount_a = coinAIsToken1;

  const liq = ClmmPoolUtil.estLiquidityAndcoinAmountFromOneAmounts(
    tickLower,
    tickUpper,
    token1Base,
    fix_amount_a,
    true,
    slippage,
    curSqrt
  );

  // 6) 取 token2 的最大需要量（token2 是 coin_a 还是 coin_b）
  const token2IsCoinA = coin_a === token2;
  const token2MaxBase: BN = token2IsCoinA ? liq.tokenMaxA : liq.tokenMaxB;
  const token2MaxHuman = fromBaseUnits(token2MaxBase, basicInfo2.decimals);

  return {
    coin_a,
    coin_b,
    tickLower,
    tickUpper,
    // 你输入语义下的区间（token1/token2）
    range_token1_per_token2: { min: P12_min.toString(), max: P12_max.toString() },
    // 内部语义下的区间（coin_b/coin_a）
    range_coin_b_per_coin_a: { min: priceBA_min.toString(), max: priceBA_max.toString() },
    token2MaxBase: token2MaxBase.toString(),
    token2MaxHuman,
  };
}




function toAtomic(amountHuman: string, decimals: number, roundUp = true) {
  const x = new Decimal(amountHuman).mul(new Decimal(10).pow(decimals));
  return new BN(x.toFixed(0, roundUp ? Decimal.ROUND_UP : Decimal.ROUND_DOWN));
}

function fromAtomic(amount: BN, decimals: number) {
  return new Decimal(amount.toString()).div(new Decimal(10).pow(decimals)).toString();
}

/**
 * pool: 你贴出来的 pool 对象（至少包含 current_sqrt_price/current_tick_index/tick_spacing）
 * token1IsA: token1 是否等于 pool.coin_type_a（你这对里：USDC 是 A，slf 是 B）
 */
export function quoteToken2ByToken1_ForAddLiquidity(params: {
  pool: any;
  token1AmountHuman: string;
  decimalsA: number; // USDC=6
  decimalsB: number; // slf=9
  token1IsA: boolean; // token1=USDC => true；token1=slf => false
  rangePct?: number;  // 例如 0.1 表示 ±10%
  slippage?: number;  // 例如 0.005 表示 0.5%
}) {
  const { pool, token1AmountHuman, decimalsA, decimalsB, token1IsA } = params;
  const rangePct = params.rangePct ?? 0.1;
  const slippage = params.slippage ?? 0.005;

  const curTick = Number(pool.current_tick_index);
  const tickSpacing = Number(pool.tick_spacing ?? pool.tickSpacing);
  const curSqrtPrice = new BN(pool.current_sqrt_price);

  // 由 P = 1.0001^tick 推出：±rangePct 对应的 tick 偏移
  const deltaTick = Math.floor(Math.log(1 + rangePct) / Math.log(1.0001));

  // 先按 tick 推一个上下界，再对齐到可初始化 tick（tickSpacing 的倍数）
  const lowerTick = TickMath.getPrevInitializableTickIndex(curTick - deltaTick, tickSpacing);
  const upperTick = TickMath.getNextInitializableTickIndex(curTick + deltaTick, tickSpacing);

  // 固定哪一边
  const fixAmountA = token1IsA;

  // 注意：coinAmount 需要传“原子单位”
  const fixedAmount = toAtomic(
    token1AmountHuman,
    fixAmountA ? decimalsA : decimalsB,
    true
  );

  // SDK 会根据当前价+区间，帮你算另一边需要多少（并做 slippage 处理）
  const r = ClmmPoolUtil.estLiquidityAndcoinAmountFromOneAmounts(
    lowerTick,
    upperTick,
    fixedAmount,
    fixAmountA,
    true,        // roundUp
    slippage,    // 例如 0.005
    curSqrtPrice
  ); // 文档示例同样这么用:contentReference[oaicite:3]{index=3}

  const needOtherAtomic = fixAmountA ? r.tokenMaxB : r.tokenMaxA;
  const needOtherHuman = fromAtomic(
    needOtherAtomic,
    fixAmountA ? decimalsB : decimalsA
  );


  return {
    lowerTick,
    upperTick,
    needToken2AmountHuman: needOtherHuman,
    // 如果你后面要直接发交易，这里也顺便给你：
    tokenMaxA: r.tokenMaxA.toString(),
    tokenMaxB: r.tokenMaxB.toString(),
    liquidity: r.liquidityAmount?.toString?.() ?? r.liquidity?.toString?.(),
  };
}




type TokenInfo = { coinType: string; decimals: number; symbol?: string;[k: string]: any };
type PoolLike = {
  id: string;
  coin_type_a: string;
  coin_type_b: string;
  current_sqrt_price: string | number | bigint;
  current_tick_index: number;
  tick_spacing: number;
};

function toBaseUnitsBN(amountHuman: string, decimals: number): BN {
  const x = new Decimal(amountHuman || "0");
  if (x.lte(0)) return new BN(0);
  const base = x.mul(new Decimal(10).pow(decimals)).toFixed(0); // 向下取整
  return new BN(base);
}

function toHuman(amountBase: BN, decimals: number): string {
  return new Decimal(amountBase.toString()).div(new Decimal(10).pow(decimals)).toString();
}

/** 把 basicInfo1/2 对齐到 pool 的 coin_type_a/b（强烈推荐这么做） */
export function alignTokenInfosToPool(pool: PoolLike, b1: TokenInfo, b2: TokenInfo) {
  const m = new Map<string, TokenInfo>([
    [b1.coinType, b1],
    [b2.coinType, b2],
  ]);

  const tokenA = m.get(pool.coin_type_a);
  const tokenB = m.get(pool.coin_type_b);
  if (!tokenA || !tokenB) {
    throw new Error("basicInfo coinType not match pool coin_type_a/b");
  }

  const token1IsA = b1.coinType === pool.coin_type_a; // 你的 token1（basicInfo1）是否为 A
  return { tokenA, tokenB, token1IsA };
}

/** 以“价格±rangePct”近似换成 tick 距离（Uniswap tick: price ~ 1.0001^tick） */
function calcTickRangeByPct(currentTick: number, tickSpacing: number, rangePct: number) {
  const step = Math.log(1.0001);
  const delta = Math.floor(Math.log(1 + rangePct) / step); // 10% => ~953 ticks

  let lower = currentTick - delta;
  let upper = currentTick + delta;

  // 对齐到可初始化 tick（必须对齐 tickSpacing）
  lower = TickMath.getPrevInitializableTickIndex(lower, tickSpacing);
  upper = TickMath.getNextInitializableTickIndex(upper, tickSpacing);

  if (lower >= upper) throw new Error("invalid tick range");
  return { lower, upper };
}

/** 固定 token1Amount，估算需要的 token2Amount（并返回 tick 范围） */
export function quoteNeedToken2ForNewPosition(params: {
  pool: PoolLike;
  basicInfo1: TokenInfo;        // UI token1
  basicInfo2: TokenInfo;        // UI token2
  token1AmountHuman: string;    // 固定侧数量（人类单位字符串）
  rangePct: number;             // 0.1 表示 ±10%
  slippage: number;             // 0.005 表示 0.5%（你自己统一口径）
}) {
  const { pool, basicInfo1, basicInfo2, token1AmountHuman, rangePct, slippage } = params;

  const { tokenA, tokenB, token1IsA } = alignTokenInfosToPool(pool, basicInfo1, basicInfo2);

  const fix_amount_a = token1IsA; // 固定 A 还是固定 B
  const coinAmountFixed = toBaseUnitsBN(token1AmountHuman, token1IsA ? tokenA.decimals : tokenB.decimals);

  const curSqrt = new BN(pool.current_sqrt_price.toString()); // 千万别用 Number，会溢出导致 range 报错
  const { lower, upper } = calcTickRangeByPct(pool.current_tick_index, pool.tick_spacing, rangePct);

  // 估算：返回 tokenMaxA / tokenMaxB（带滑点上限）
  const est = ClmmPoolUtil.estLiquidityAndcoinAmountFromOneAmounts(
    lower,
    upper,
    coinAmountFixed,
    fix_amount_a,
    true,        // roundUp（按你项目风格）
    slippage,
    curSqrt
  );

  const needToken2Base = fix_amount_a ? est.tokenMaxB : est.tokenMaxA;
  const needToken2Human = fix_amount_a
    ? toHuman(needToken2Base, tokenB.decimals)
    : toHuman(needToken2Base, tokenA.decimals);

  return {
    tokenA,
    tokenB,
    token1IsA,
    tickLower: lower,
    tickUpper: upper,
    needToken2Human,
    est, // 里面还有 liquidity、tokenMaxA/B
  };
}


export async function openNewPositionWithFixedToken1(params: {
  sdk: any;            // CetusClmmSDK 实例（@cetusprotocol/cetus-sui-clmm-sdk）
  pool: PoolLike;
  basicInfo1: TokenInfo;
  basicInfo2: TokenInfo;
  token1AmountHuman: string;
  rangePct: number;
  slippage: number;
}) {
  const q = quoteNeedToken2ForNewPosition(params);

  // 固定侧金额：amount_a 或 amount_b
  const fixedBase = toBaseUnitsBN(params.token1AmountHuman, q.token1IsA ? q.tokenA.decimals : q.tokenB.decimals);

  const amount_a = q.token1IsA ? fixedBase.toString() : q.est.tokenMaxA.toString();
  const amount_b = q.token1IsA ? q.est.tokenMaxB.toString() : fixedBase.toString();
  console.log("params.pool.coin_type_a", params.pool.coin_type_a);
  console.log(" params.pool.coin_type_b", params.pool.coin_type_b)
  console.log("amount_a", amount_a);
  console.log("amount_b", amount_b);
  console.log("q", q);
  const payload = await params.sdk.Position.createAddLiquidityFixTokenPayload({
    pool_id: params.pool.id,
    pos_id: "0x0",                 // is_open=true 时通常不会用到，给个占位即可
    coin_type_a: params.pool.coin_type_a,
    coin_type_b: params.pool.coin_type_b,

    tick_lower: q.tickLower,
    tick_upper: q.tickUpper,

    amount_a,
    amount_b,

    fix_amount_a: q.token1IsA,
    is_open: true,                 // ✅ 新开 position
    slippage: params.slippage,

    collect_fee: false,
    rewarder_coin_types: [],
  });

  console.log("payload", payload)
  // 你这里：
  // - 私钥模式：client.signAndExecuteTransaction({ signer, transaction: payload, ... })
  // - 钱包模式：signAndExecuteTransaction({ transaction: await payload.toJSON(), chain:'sui:mainnet' })
  return { payload, needToken2AmountHuman: q.needToken2Human, tickLower: q.tickLower, tickUpper: q.tickUpper };
}
